import * as CL from '@design-system/component-library';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { upsertContactUser } from '../../common/fetch.js';
import {
  accessRightsMsg,
  addAccessToOtherCompaniesAdditionalInformationMsg,
  addAccessToOtherCompaniesMsg,
  confirmMsg,
  editMsg,
  noRightsMsg,
  orderAcceptorAdditionalInformationMsg,
  orderAcceptorMsg,
  otherCompaniesMsg,
  savingTheDataFailedMsg,
  t,
} from '../../common/i18n/index.js';
import { getCompanyName } from '../../common/utils/accountUtils.js';
import { deepEqual } from '../../common/utils/objectUtils.js';
import { AccountStatus, AuthenticatedUserRole, ContactRole } from '../../generated/api/models.js';
import { addNotification } from '../../public/site/notificationStore.js';
import { ButtonGroupForSubmitAndBack } from '../ButtonGroupForSubmitAndBack/ButtonGroupForSubmitAndBack.js';
import { LinkableAccordion } from '../LinkableAccordion/index.js';
import { ToggleCheckbox } from '../ToggleCheckbox/ToggleCheckbox.js';
import type { DialogParams } from '../../common/types/dialog.js';
import type { AccountContactRelationship, AccountHeader, Contact } from '../../generated/api/models.js';
import type { AuthenticatedUser } from '../../public/site/authState';

import './UserRolesAndAdminRights.scss';

interface UserRolesAndAdminRightsProps {
  isNewDisconnectedContact: boolean;
  ignoreAccordion: boolean;
  companyName?: string;
  contact?: Contact;
  secondaryAccounts?: AccountHeader[];
  onShowDialog: (params: DialogParams) => void;
  onChangeApproverRole?: (isApprover: boolean) => void;
  mdmId?: string;
  authenticatedUser: AuthenticatedUser;
  onBeforeEdit?: () => boolean;
}

const isContactInitiallyAdmin = (contact?: Contact): boolean => {
  return contact?.person?.userProfile === AuthenticatedUserRole.KEY_USER;
};

const isContactInitiallyApprover = (contact?: Contact): boolean => {
  return contact?.person?.roles?.includes(ContactRole.APPROVER) || false;
};

const getSecondaryACRs = (accountContactRelationships: AccountContactRelationship[]) =>
  accountContactRelationships.filter(acr => !acr.isPrimaryAccount);

const getSecondaryACRsFromContact = (contact?: Contact): string[] => {
  return getSecondaryACRs(contact?.accountContactRelationships || [])?.map(account => account.accountMasterId) || [];
};

const getSecondaryACRsCompanyName = (
  secondaryACRsMdmIds: string[],
  secondaryAccountsHeader: AccountHeader[]
): string[] => {
  return secondaryACRsMdmIds
    .map(secondaryACRsMdmId => {
      const accountHeader = secondaryAccountsHeader.find(item => item.accountMasterId === secondaryACRsMdmId);
      if (accountHeader) {
        return accountHeader.accountName;
      } else {
        return '';
      }
    })
    .filter(e => e !== '');
};

const isAccountCheckboxSelected = (accountMasterId: string, items: string[]): boolean | undefined => {
  return items.includes(accountMasterId);
};

const toggleElement = (arr: string[], val: string) =>
  arr.includes(val) ? arr.filter(el => el !== val) : [...arr, val];

const getTitleForSection = (isContactApprover: boolean) => {
  if (isContactApprover) {
    return (
      <div>
        <CL.Icon icon="check" size="s" color="success" type="filled" /> {t.POX2(orderAcceptorMsg)}
      </div>
    );
  } else {
    return <div>{t.VXXY(noRightsMsg)}</div>;
  }
};

const getUpdatedSecondaryACRs = (selectedSecondaryAccounts: string[], contact?: Contact) => {
  const existingSecondaryACRs = getSecondaryACRs(contact?.accountContactRelationships || []);
  const updatedSecondaryACRs: AccountContactRelationship[] = selectedSecondaryAccounts.map(accountMasterId => {
    return {
      isPrimaryAccount: false,
      accountMasterId,
    };
  });
  const isSecondaryACRsUpdated = !deepEqual(existingSecondaryACRs, updatedSecondaryACRs);

  return {
    isSecondaryACRsUpdated,
    updatedSecondaryACRs,
  };
};

function getSecondaryCompanyNamesSection(secondaryACRsCompanyName: string[]) {
  return (
    <>
      <h5 className={dsClass.MARGIN_BOTTOM_1}>{t.Y37N(otherCompaniesMsg)}</h5>
      <div>
        <CL.Icon icon="check" size="s" color="success" type="filled" />{' '}
        <span>
          {t.E112('Administrator')}: {secondaryACRsCompanyName.join(', ')}
        </span>
      </div>
    </>
  );
}

export const UserRolesAndAdminRights = (props: UserRolesAndAdminRightsProps) => {
  const navigate = useNavigate();
  const { search } = useLocation();

  const {
    mdmId,
    contact,
    ignoreAccordion,
    isNewDisconnectedContact,
    onBeforeEdit,
    onChangeApproverRole,
    authenticatedUser,
  } = props;
  const companyName = getCompanyName(authenticatedUser, mdmId);

  const [isEdit, setIsEdit] = useState(isNewDisconnectedContact || ignoreAccordion);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [secondaryACRs, setSecondaryACRs] = useState(() => getSecondaryACRsFromContact(contact) || []);
  const [isContactApprover, setIsContactApprover] = useState(() => isContactInitiallyApprover(contact));
  const [showLimitedAccountsList, setShowLimitedAccountsList] = useState(true);

  const isContactAdmin = isContactInitiallyAdmin(contact);
  const otherAccessibleAccountsWithUserRightsInSalesforce = authenticatedUser?.secondaryAccounts || [];

  const currentAdminAccount = {
    accountMasterId: authenticatedUser.mdmId!,
    accountName: authenticatedUser.companyName!,
    businessId: authenticatedUser.businessId!,
    accountStatus: AccountStatus.ACTIVE,
    deviceStoreInOec: authenticatedUser.deviceStoreInOec,
    userRightsInSalesforce: authenticatedUser.userRightsInSalesforce,
  } satisfies AccountHeader;

  // because we are using company selection from dropdown,
  // we need to combine "secondary" accounts (i.e. accessible accounts other than active account)
  // with the active account to have a list of all accounts,
  // and then filter out the Contact's mdmId for "Add administrator rights to other companies"
  const accountsWithUserRightsInSalesforce =
    [...otherAccessibleAccountsWithUserRightsInSalesforce, currentAdminAccount].filter(
      (acc: AccountHeader) => acc.userRightsInSalesforce === true && acc.accountMasterId !== mdmId
    ) || [];

  const maxAccountSelectionCount = showLimitedAccountsList ? 20 : accountsWithUserRightsInSalesforce?.length || 20;
  const visibleSecondaryAccounts = showLimitedAccountsList
    ? accountsWithUserRightsInSalesforce?.slice(0, maxAccountSelectionCount) || []
    : accountsWithUserRightsInSalesforce;
  const showMoreAccountsLink =
    showLimitedAccountsList &&
    accountsWithUserRightsInSalesforce &&
    accountsWithUserRightsInSalesforce.length > maxAccountSelectionCount;

  const submitContact = async (contactToBeUpdated: Contact) => {
    const updatedContact: Contact = JSON.parse(JSON.stringify(contactToBeUpdated));

    if (!updatedContact.contactId || !authenticatedUser?.mdmId) {
      return;
    }

    const { isSecondaryACRsUpdated, updatedSecondaryACRs } = getUpdatedSecondaryACRs(secondaryACRs, contact);
    if (isSecondaryACRsUpdated) {
      updatedContact.accountContactRelationships = updatedSecondaryACRs;
    }

    let approverRoleUpdated = false;

    if (updatedContact.person) {
      // Append approver role if selected and doesn't already exist
      if (isContactApprover && !contact?.person?.roles?.includes(ContactRole.APPROVER)) {
        updatedContact.person.roles?.push(ContactRole.APPROVER);
        approverRoleUpdated = true;
      }
      // Remove approver role if removed from selection
      if (!isContactApprover && contact?.person?.roles?.includes(ContactRole.APPROVER)) {
        updatedContact.person.roles = updatedContact.person.roles?.filter(role => role !== ContactRole.APPROVER);
        approverRoleUpdated = true;
      }
    }

    setIsSubmitting(true);

    try {
      if (isSecondaryACRsUpdated || approverRoleUpdated) {
        await upsertContactUser(updatedContact, false, undefined, undefined, mdmId);
        addNotification(t.FFJ2('Changes were successfully saved'), 'success');
      }
      navigate(search, { replace: true });
      setIsEdit(false);
    } catch (err) {
      addNotification(t.RQA1(savingTheDataFailedMsg), 'warning');
    } finally {
      setIsSubmitting(false);
    }
  };

  const accountsSelectionSection = (accounts: AccountHeader[]) => (
    <div className="of-contact-access-rights-accordion__content__account-selections">
      {accounts.map(item => (
        <CL.Checkbox
          key={item.accountMasterId}
          checked={isAccountCheckboxSelected(item.accountMasterId, secondaryACRs)}
          onChange={() => {
            const acrs = toggleElement(secondaryACRs, item.accountMasterId);
            setSecondaryACRs(acrs);
          }}
        >
          {item.accountName}
        </CL.Checkbox>
      ))}
      {showLimitedAccountsList && showMoreAccountsLink && (
        <div className="of-contact-access-rights-accordion__content__account-selections--show-all-link">
          <CL.Button color="link" onClick={() => setShowLimitedAccountsList(false)}>
            {t.SF4C('Show all')}
          </CL.Button>
        </div>
      )}
    </div>
  );

  const onUpdateApprovalRole = (isApprover: boolean) => {
    setIsContactApprover(isApprover);
    onChangeApproverRole && onChangeApproverRole(isApprover);
  };

  const requiredInformationMissing =
    !contact?.person?.firstName ||
    !contact?.person?.lastName ||
    !contact?.person?.phoneNumber ||
    !contact?.person?.email;

  const handleEditClick = () => {
    if (!onBeforeEdit || onBeforeEdit()) {
      setIsEdit(true);
    }
  };

  const content = (
    <>
      <div className="of-contact-access-rights-accordion__content">
        <h5 className={`of-contact-access-rights-accordion__content-title ${dsClass.MARGIN_BOTTOM_1}`}>
          {companyName}
        </h5>
        {!isEdit && getTitleForSection(isContactInitiallyApprover(contact))}
        {!isEdit &&
          secondaryACRs.length > 0 &&
          authenticatedUser?.secondaryAccounts &&
          getSecondaryCompanyNamesSection(
            getSecondaryACRsCompanyName(secondaryACRs, authenticatedUser.secondaryAccounts)
          )}
        {isEdit && (
          <div className="of-contact-access-rights-accordion__content-options">
            {requiredInformationMissing && !isNewDisconnectedContact && (
              <CL.Disclaimer
                title={t.UV5C('Contact information is missing')}
                text={t.HP5L('User must have a name, phone number and email address in order to add access rights.')}
                disclaimerType="warning"
                icon={<CL.Icon icon="notification" type="regular" aria-hidden="true" />}
                visible
              />
            )}
            <div>
              <ToggleCheckbox
                title={t.POX2(orderAcceptorMsg)}
                additionalInformation={t.UCRF(orderAcceptorAdditionalInformationMsg)}
                initialValue={isContactApprover}
                onChange={onUpdateApprovalRole}
                disabled={!isNewDisconnectedContact && requiredInformationMissing}
              />
            </div>
          </div>
        )}
        {isEdit &&
          isContactAdmin &&
          accountsWithUserRightsInSalesforce &&
          accountsWithUserRightsInSalesforce?.length > 0 && (
            <>
              <h5>
                {t.Y37N(otherCompaniesMsg)} ({accountsWithUserRightsInSalesforce?.length || 0})
              </h5>
              {isContactAdmin && authenticatedUser?.userRightsInSalesforce && (
                <div>
                  <div className={`of-contact-access-rights-accordion__other_companies_section ${dsClass.PADDING_3}`}>
                    <div>
                      <span>{t.FD6H(addAccessToOtherCompaniesMsg)}</span>
                    </div>
                    <div>
                      <span className={dsClass.FONT_SIZE_SMALL}>
                        {t.HWZF(addAccessToOtherCompaniesAdditionalInformationMsg)}
                      </span>
                    </div>
                    {accountsSelectionSection(visibleSecondaryAccounts)}
                  </div>
                </div>
              )}
            </>
          )}
      </div>
      <div className="of-contact-access-rights-accordion__buttons">
        {isEdit && !isNewDisconnectedContact && contact && (
          <ButtonGroupForSubmitAndBack
            onCancel={() => {
              setSecondaryACRs(getSecondaryACRsFromContact(contact) || []);
              setIsEdit(false);
            }}
            onSubmit={() => submitContact(contact)}
            submitButtonText={t.QVYK(confirmMsg)}
            submitting={isSubmitting}
            submitDisabled={requiredInformationMissing}
            className="of-contact-user-rights-accordion__button-group"
            submitOnLeft={true}
          />
        )}
        {!isEdit && (
          <CL.Button type="button" color="light" onClick={handleEditClick}>
            {t.NVPK(editMsg)}
          </CL.Button>
        )}
      </div>
    </>
  );

  if (ignoreAccordion) {
    return content;
  } else {
    return (
      <LinkableAccordion id="access-rights-accordion" heading={t.Y8LP(accessRightsMsg)}>
        {content}
      </LinkableAccordion>
    );
  }
};
